import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Mail, Phone, MessageSquare, UserPlus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// Visible card-page logo (dark transparent ORB wordmark — designed for
// the white card surface).
const ORB_LOGO = "https://customer-assets.emergentagent.com/job_4f2b8787-9e39-421b-9343-dec62de2fe7e/artifacts/jrh9uqnh_Untitled%20design%20%287%29.png";
// iter175bj — Tab favicon / Add-to-Home-Screen icon. Uses the green
// branded ORB favicons baked into /public (the same ones index.html
// links to). The transparent dark wordmark above renders invisible
// inside iOS Safari's tab strip and on dark home-screen wallpapers,
// so we explicitly serve the branded green PNGs here.
const FAVICON_GREEN_180 = "/favicon-180.png?v=2026-05-17";
const FAVICON_GREEN_48 = "/favicon-48.png?v=2026-05-17";
const FAVICON_GREEN_32 = "/favicon-32.png?v=2026-05-17";
const FAVICON_GREEN_16 = "/favicon-16.png?v=2026-05-17";
const FAVICON_GREEN_192 = "/icons/icon-192x192.png?v=2026-05-17";

/**
 * Email-signature CALL gate (iter175bg).
 *
 * Renders a STANDALONE clone of the business-card visual (ORB logo,
 * photo, name, title, company, action icons) blurred behind the
 * "Please provide your information" sheet. Does NOT import or touch
 * the BusinessCardContent component — every class/style on the
 * visible card is duplicated verbatim from BusinessCardContent so
 * the visual parity is exact.
 *
 * Sheet differences from the regular card contact popup:
 *   • heading reads "Please provide your information so we can route
 *     your call to {first_name}"
 *   • the notes textarea is omitted
 *   • the submit button is labelled "Call {first_name}"
 *   • the sheet is NOT collapsible (no swipe-down, no backdrop
 *     dismiss, no drag handle)
 *
 * On submit the page calls /api/c/sig-call/{token}/dial which records
 * a 30-min EXACT (caller_phone, target_user_id) pre-assignment and
 * returns a tel: URI — the page then hands off to the iOS dialer.
 */
const SigCallPage = () => {
  const { token } = useParams();
  const [cardData, setCardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const wrapRef = useRef(null);
  const [centerOffset, setCenterOffset] = useState(0);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', notes: '', sms_consent: false });
  const [submitting, setSubmitting] = useState(false);

  // iOS safe-area / theme — black to match the public card page.
  // PLUS immediate ORB favicon injection so SOMETHING shows up
  // before the /api/card fetch resolves. The dependent effect
  // below upgrades the icon to the user's photo when one is on
  // file.
  useEffect(() => {
    const prevHtmlBg = document.documentElement.style.background;
    const prevBodyBg = document.body.style.background;
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    const prevTheme = themeMeta ? themeMeta.getAttribute('content') : null;
    document.documentElement.style.background = '#000';
    document.body.style.background = '#000';
    if (themeMeta) themeMeta.setAttribute('content', '#000000');

    // iter175bj — Inject the GREEN branded ORB favicons IMMEDIATELY
    // on mount (don't wait for cardData). The transparent dark
    // wordmark used on the card surface is invisible in iOS Safari's
    // tab strip / Add-to-Home-Screen prompt, so we always start with
    // the green PNGs from /public. The cardData-keyed effect below
    // will swap to the user's profile photo when one is on file.
    const head = document.head;
    const originalIcons = [];
    head.querySelectorAll("link[rel*='icon']").forEach((el) => {
      originalIcons.push(el);
      el.parentNode.removeChild(el);
    });
    const inject = (rel, sizes, href) => {
      const l = document.createElement('link');
      l.rel = rel;
      if (sizes) l.setAttribute('sizes', sizes);
      l.type = 'image/png';
      l.href = `${href}${href.includes('?') ? '&' : '?'}sigcall=${Date.now()}`;
      l.dataset.sigCallTemp = '1';
      head.appendChild(l);
    };
    inject('apple-touch-icon', null, FAVICON_GREEN_180);
    inject('apple-touch-icon', '180x180', FAVICON_GREEN_180);
    inject('icon', '192x192', FAVICON_GREEN_192);
    inject('icon', '48x48', FAVICON_GREEN_48);
    inject('icon', '32x32', FAVICON_GREEN_32);
    inject('icon', '16x16', FAVICON_GREEN_16);
    inject('shortcut icon', null, FAVICON_GREEN_180);

    return () => {
      document.documentElement.style.background = prevHtmlBg;
      document.body.style.background = prevBodyBg;
      if (themeMeta && prevTheme !== null) themeMeta.setAttribute('content', prevTheme);
      Array.from(head.querySelectorAll("link[data-sig-call-temp='1']")).forEach((el) => el.remove());
      originalIcons.forEach((el) => head.appendChild(el));
    };
  }, []);

  // iter175bk — Favicon is locked to the GREEN branded ORB logo and
  // never swaps to the user's profile photo. iOS Safari was rejecting
  // / failing to render arbitrary profile_picture URLs as
  // apple-touch-icon (resulting in a BLANK tab icon on mobile), so we
  // keep the favicon stationary on the branded asset that iOS already
  // accepts for the rest of the app.

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/card/${token}`);
        if (!res.ok) throw new Error('Card not found');
        const data = await res.json();
        data.id = token;
        setCardData(data);
      } catch (_e) {
        setError('Card not found');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  // iter175bg — Document title in iOS Safari's tab/share UI. Sets
  // "Call <FirstName>" once cardData is resolved, matching the
  // older `_SIG_CALL_FORM_HTML` page title that read the same way.
  // Restores the prior title on unmount so nav-back doesn't leak.
  useEffect(() => {
    if (!cardData) return;
    const fn = (
      cardData.first_name
      || (cardData.full_name || cardData.name || '').split(' ')[0]
      || 'them'
    ).trim();
    const prevTitle = document.title;
    document.title = `Call ${fn}`;
    return () => { document.title = prevTitle; };
  }, [cardData]);

  // iPad portrait centering (mirrors PublicBusinessCardPage).
  useEffect(() => {
    if (!wrapRef.current) return;
    const isTabletPortrait = () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(min-width: 768px) and (orientation: portrait)').matches;
    const measure = () => {
      if (!isTabletPortrait()) { setCenterOffset(0); return; }
      const cardEl = wrapRef.current.querySelector('[data-testid="sig-call-card-container"]');
      if (!cardEl) return;
      const vh = window.innerHeight;
      const cardH = cardEl.getBoundingClientRect().height;
      setCenterOffset(Math.max(0, Math.floor((vh - cardH) / 2 - 20)));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [cardData]);

  if (loading) {
    return (
      <div className="h-screen bg-black flex items-center justify-center" data-testid="sig-call-loading">
        <Loader2 className="w-8 h-8 text-white/50 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen bg-black flex items-center justify-center p-6" data-testid="sig-call-error">
        <div className="text-center">
          <img src={ORB_LOGO} alt="ORB" className="h-12 mx-auto mb-6 object-contain" />
          <p className="text-gray-500 text-lg">{error}</p>
        </div>
      </div>
    );
  }

  const firstName = (
    cardData?.first_name
    || (cardData?.full_name || cardData?.name || '').split(' ')[0]
    || 'them'
  ).trim();
  const name = cardData?.full_name || cardData?.name || '';
  const title = cardData?.title || '';
  const company = cardData?.company || 'Collective Solutions Inc.';
  const profilePicture = cardData?.profile_picture || cardData?.photo_url || '';
  const initials = (name || '').split(' ').map(p => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || 'U';

  const submit = async () => {
    if (!form.first_name || !form.last_name || !form.email || !form.phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/c/sig-call/${encodeURIComponent(token)}/dial`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          caller_phone: form.phone,
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email,
          notes: form.notes,
          sms_consent: !!form.sms_consent,
          kind: 'call',
        }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.tel_uri) {
        toast.error(j?.detail || 'Could not start the call. Please try again.');
        setSubmitting(false);
        return;
      }
      setTimeout(() => { window.location.href = j.tel_uri; }, 0);
    } catch (_e) {
      toast.error('Network error. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div
      ref={wrapRef}
      className="fixed inset-0 bg-black overflow-y-auto flex flex-col items-center"
      style={{
        paddingTop: `calc(env(safe-area-inset-top, 0px) + 20px + ${centerOffset}px)`,
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)',
      }}
      data-testid="sig-call-page"
    >
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css" />

      {/* ===== Card visual — standalone copy of the visible portion of
                BusinessCardContent's top block. Classes are duplicated
                verbatim so this page renders identically to the public
                card behind its contact popup. ===== */}
      <div
        className="w-[92vw] max-w-[420px] bg-white border-2 border-black rounded-3xl flex flex-col items-center relative overflow-hidden shadow-2xl"
        style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Helvetica Neue', Arial, sans-serif" }}
        data-testid="sig-call-card-container"
      >
        <div className="w-full flex flex-col items-center px-6 pt-[2vh] pb-4">
          <img src={ORB_LOGO} alt="ORB" className="h-[11vh] object-contain mb-[1vh]" />
          <div className="mb-[0.8vh]">
            <Avatar className="h-[22vh] w-[16vh] rounded-2xl border-2 border-gray-200">
              <AvatarImage src={profilePicture} className="object-cover object-top" style={{ aspectRatio: 'auto' }} />
              <AvatarFallback className="bg-gradient-to-br from-[#586D52] to-[#3a4d36] text-white text-6xl rounded-2xl">
                {initials}
              </AvatarFallback>
            </Avatar>
          </div>
          <h1 className="text-2xl font-extrabold text-black leading-tight tracking-tight">{name}</h1>
          {title ? <p className="text-gray-600 text-base capitalize font-medium">{title}</p> : null}
          <p className="text-gray-500 text-[11px] tracking-[0.2em] uppercase mb-[1.2vh] font-semibold">{company}</p>
          <div className="w-full h-px bg-gray-200 mt-[0.55vh] mb-[1.2vh]" />
          <div className="flex items-center justify-center gap-4 mb-[1.2vh]">
            {[
              { icon: <Mail className="w-5 h-5" />, label: 'Email' },
              { icon: <Phone className="w-5 h-5" />, label: 'Call' },
              { icon: <MessageSquare className="w-5 h-5" />, label: 'Text' },
              { icon: <UserPlus className="w-5 h-5" />, label: 'Contact' },
            ].map((item) => (
              <div key={item.label} className="flex flex-col items-center gap-0.5">
                <div className="h-11 w-11 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-700">
                  {item.icon}
                </div>
                <span className="text-gray-600 text-[11px] font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== Backdrop — same z-90 black/50 + backdrop-blur-sm as the
                business card contact popup overlay, but NO click handler:
                the sig-call popup is NOT collapsible. ===== */}
      <div className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm" />

      {/* ===== Sheet — same exact classes as the business card contact
                popup, minus: drag-handle pip, swipe handlers, notes
                textarea. Heading + submit button are the only copy
                differences. ===== */}
      <div
        className="fixed inset-x-0 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-[7.5in] bottom-0 z-[100] bg-gradient-to-b from-[#f6faf3] via-[#7f8d79] via-30% via-[#222] via-55% to-black rounded-t-3xl overflow-hidden flex flex-col shadow-[0_-20px_60px_rgba(0,0,0,0.35)] border-t border-white/40"
        style={{
          // iter175bg — Bump the popup 25px higher than the card
          // contact-popup default. iOS Safari's bottom toolbar /
          // home-indicator area was clipping the CTA on smaller
          // iPhones and the body's black background was visible
          // through it.
          top: 'calc(42vh + 40px)',
          fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Helvetica Neue', Arial, sans-serif",
        }}
        data-testid="sig-call-popup"
      >
        <div className="flex-1 overflow-hidden px-6 pt-5 pb-6 space-y-3" style={{ paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))', overscrollBehavior: 'contain' }}>
          <h3 className="text-lg font-bold text-center text-gray-900" data-testid="sig-call-heading">
            Please provide your information so we can route your call to {firstName}
          </h3>

          <div className="flex gap-2">
            <Input placeholder="First Name *" value={form.first_name} onChange={e => setForm(p => ({ ...p, first_name: e.target.value }))} className="text-sm h-11 bg-white border-gray-200 rounded-xl text-black placeholder:text-gray-400" data-testid="sig-call-first-name" />
            <Input placeholder="Last Name *" value={form.last_name} onChange={e => setForm(p => ({ ...p, last_name: e.target.value }))} className="text-sm h-11 bg-white border-gray-200 rounded-xl text-black placeholder:text-gray-400" data-testid="sig-call-last-name" />
          </div>

          <div className="flex gap-2">
            <Input type="email" placeholder="Email *" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="text-sm h-11 bg-white border-gray-200 rounded-xl text-black placeholder:text-gray-400" data-testid="sig-call-email" />
            <Input type="tel" placeholder="Phone *" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="text-sm h-11 bg-white border-gray-200 rounded-xl text-black placeholder:text-gray-400" data-testid="sig-call-phone" />
          </div>

          <textarea
            placeholder="Notes (optional)"
            value={form.notes}
            onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm resize-none h-16 bg-white text-black placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#586D52]/30"
            data-testid="sig-call-notes"
          />

          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.sms_consent}
              onChange={e => setForm(p => ({ ...p, sms_consent: e.target.checked }))}
              className="mt-0.5 accent-[#586D52] h-4 w-4 shrink-0"
              data-testid="sig-call-sms-consent"
            />
            <span className="text-[11px] text-white leading-snug">
              By checking this box, you agree to receive sms messages from us. Message frequency varies & data rates may apply. Reply stop to opt out.{' '}
              <a href="https://www.collectiveorb.com/privacy-policy" target="_blank" rel="noreferrer" className="underline text-white font-medium">Privacy Policy</a>.
            </span>
          </label>

          <Button
            className="w-full bg-[#586D52] hover:bg-[#4a5d46] text-white text-base font-bold py-3 rounded-xl shadow-md"
            onClick={submit}
            disabled={submitting}
            data-testid="sig-call-submit-btn"
          >
            {submitting ? 'Connecting…' : `Call ${firstName}`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SigCallPage;
